/**
 * @fileoverview Hilfsfunktionen für die Mitarbeiterverwaltung
 */

/**
 * Wandelt Excel-Daten in ein geeignetes Format für die Jahresplanung um
 * 
 * @param {Array<Array>} excelData - Die Rohdaten aus der Excel-Datei
 * @return {Object} Die formatierten Daten für die Jahresplanung
 */
export const processAnnualPlanData = (excelData) => {
  try {
    if (!excelData || !Array.isArray(excelData) || excelData.length === 0) {
      throw new Error('Ungültige Daten - Keine Daten gefunden');
    }

    const result = {};
    
    // Annahme: Die erste Spalte jeder Zeile enthält das Datum im Format "YYYY-MM-DD"
    excelData.forEach(row => {
      if (row.length < 2 || !row[0]) return;

      const dateString = row[0].toString();
      if (!dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return;

      const date = dateString;
      const plannedHours = parseFloat(row[1]) || 0;
      
      result[date] = {
        date,
        plannedHours,
      };
    });

    return result;
  } catch (error) {
    console.error('Fehler bei der Verarbeitung der Jahresplan-Daten:', error);
    throw error;
  }
};

/**
 * Wandelt Excel-Daten in ein geeignetes Format für die Urlaubsplanung um
 * 
 * @param {Array<Array>} excelData - Die Rohdaten aus der Excel-Datei
 * @param {string} employeeId - Die ID des Mitarbeiters
 * @return {Object} Die formatierten Daten für die Urlaubsplanung
 */
export const processHolidayData = (excelData, employeeId) => {
  try {
    if (!excelData || !Array.isArray(excelData) || excelData.length === 0) {
      throw new Error('Ungültige Daten - Keine Daten gefunden');
    }

    const holidayEntries = [];
    
    // Annahme: Spalten sind Datum, Status (Urlaub/Krank)
    excelData.forEach(row => {
      if (row.length < 2 || !row[0]) return;

      const dateString = row[0].toString();
      if (!dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return;

      const status = (row[1] || '').toString().toLowerCase();
      if (status !== 'urlaub' && status !== 'krank') return;
      
      holidayEntries.push({
        date: dateString,
        employeeId,
        status: status === 'urlaub' ? 'Urlaub' : 'krank',
        startTime: '00:00',
        endTime: '00:00'
      });
    });

    return {
      employeeId,
      timeslots: holidayEntries
    };
  } catch (error) {
    console.error('Fehler bei der Verarbeitung der Urlaubsdaten:', error);
    throw error;
  }
};

/**
 * Überprüft, ob eine Datei im XLSX-Format ist
 * 
 * @param {File} file - Die zu überprüfende Datei
 * @return {boolean} True, wenn es sich um eine XLSX-Datei handelt
 */
export const isValidExcelFile = (file) => {
  const validTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
  ];
  return file && validTypes.includes(file.type);
};

/**
 * Formatiert eine Fehlermeldung für die Anzeige
 * 
 * @param {Error} error - Das Fehlerobjekt
 * @return {string} Die formatierte Fehlermeldung
 */
export const formatErrorMessage = (error) => {
  if (!error) return '';
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return 'Ein unbekannter Fehler ist aufgetreten';
};

export default {
  processAnnualPlanData,
  processHolidayData,
  isValidExcelFile,
  formatErrorMessage
};
